import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  getPatientAddresses,
  searchPatients,
  searchPatientsByGps,
} from '@/services/massager/visit.service';
import type {
  PatientAddressResponse,
  PatientSearchResponse,
} from '@/types/massager/visit';

/**
 * Explicit-trigger search: we run searchPatients only when the user presses
 * 検索, not on every keystroke. A mutation fits better than a query here
 * because the keyword is ephemeral and we want full control over refetch.
 */
export function usePatientSearch() {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();

  return useMutation<PatientSearchResponse, Error, { name: string; targetDate: string }>({
    mutationFn: ({ name, targetDate }) =>
      searchPatients(clinic?.site_type ?? '', name, targetDate),
    onError: err => toast.error(getApiErrorMessage(err, '受診者検索に失敗しました。')),
  });
}

/**
 * GPS-based patient lookup for the add-visit GPS flow. Mutation rather than
 * query: the parent (page) controls when to trigger by passing fresh
 * coordinates after each geolocation request.
 *
 * Returns the same shape as {@link usePatientSearch} so the consuming form
 * can merge candidates from either source.
 */
export function useGpsPatientSearch() {
  const { clinic } = useMassagerAuthContext();

  return useMutation<
    PatientSearchResponse,
    Error,
    { latitude: number; longitude: number; targetDate: string; serviceType?: 'A2' | 'R2' | null }
  >({
    mutationFn: ({ latitude, longitude, targetDate, serviceType }) =>
      searchPatientsByGps(clinic?.site_type ?? '', {
        latitude,
        longitude,
        target_date: targetDate,
        service_type: serviceType ?? null,
      }),
    // Intentionally no toast on error — the consuming form surfaces the
    // backend failure via the same in-card message line as the geolocation
    // errors so the massager only sees one place for feedback.
  });
}

/**
 * Address list for a selected patient. Pass null to skip fetching.
 * `targetDate` (YYYY-MM-DD) is forwarded to the API so the base address
 * (PatientInformation.is_current=1) is filtered by available_to.
 *
 * `serviceType` (R2/A2) is forwarded so the backend can also tell us whether
 * the patient has an active 同意書 with electric-treatment registered on
 * `targetDate` — used to gate the 温罨法・電療無し / 電(鍼・温)無し checkbox.
 */
export function usePatientAddresses(
  patientId: number | null,
  targetDate: string,
  serviceType?: string | null
) {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();

  const query = useQuery<PatientAddressResponse, Error>({
    queryKey: [
      'massager',
      'patient-addresses',
      clinic?.site_type ?? '',
      patientId,
      targetDate,
      serviceType ?? '',
    ],
    queryFn: () =>
      getPatientAddresses(
        clinic?.site_type ?? '',
        patientId as number,
        targetDate,
        serviceType
      ),
    enabled: !!clinic?.site_type && !!patientId && !!targetDate,
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(getApiErrorMessage(query.error, '住所取得に失敗しました。'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    addresses: query.data?.data?.addresses ?? [],
    hasElectricTreatment: query.data?.data?.has_electric_treatment ?? false,
    suddenlyVisitIs: query.data?.data?.suddenly_visit_is ?? false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
